import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Button, Chip, FormControl, MenuItem, TextField } from '@mui/material';
import { useController } from 'react-hook-form';
import { useStore } from '@/components/store/Store';
import { DMSortType, SortDirection } from '@/declarations/models/SortOption';
import { DMObjectType } from '@/declarations/models/DMObjectType';
import { MediaLicense } from '@/declarations/models/MediaLicense';
import { DMAutoBlockModel, DMViewType } from '@/declarations/models/blocks/DMBlock';
import { DimuOwner } from '@/declarations/models/DimuOwner';
import { Api } from '@/services/Api';
import { noop } from '@/utils/functions';
import Styles from '@/assets/js/Styles';
import PageListManualBlockItem from '@/editor/PageEditor/EditorBlock/PageListBlocks/PageListManualBlockItem';
import M24PageFinder from '@/components/Finder/M24PageFinder/M24PageFinder';
import Container from '../../../../components/Container';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '../../CurrentBlockProvider';
import SelectInput from '../../../../components/forms/SelectInput';
import SwitchInput from '../../../../components/forms/SwitchInput';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import TextInput from '../../../../components/forms/TextInput';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

const autocompleteItemStyle = (selected: boolean) => ({
  backgroundColor: selected ? 'rgba(0,0,0,0.16) !important' : undefined,
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.08) !important',
  },
});

const getOptionLabel = (option: DimuOwner) => `${option.name} (${option.identifier})`;
/* 
export const getMapSettings = (t: any, blockPath: BlockPath) => (
  <FormControl sx={settingsAreaStyle}>
    <FormLabel>{t('DMBlock.MapSettings')}</FormLabel>
    <FormGroup row>TODO: Add Map settings</FormGroup>
  </FormControl>
);
*/

export const DMAutoBlock: BlockComponent = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const { blockPath, block } = useCurrentBlock();
  const currentBlock = block as DMAutoBlockModel;
  const [allDimuOwners, setAllDimuOwners] = useState<Array<DimuOwner>>([]);
  const [selectedOwners, setSelectedOwners] = useState<Array<DimuOwner>>([]);
  const [pageFinderOpen, setPageFinderOpen] = useState<boolean>(false);
  const {
    state: { currentUser },
  } = useStore();
  const isSuperAdmin = currentUser?.user?.is_super_admin;

  const gridSizeOptions = ['auto', '3', '4', '5', '6'];
  const mobileGridOptions = ['auto', 'horizontal'];

  const {
    field: { value: ownerCodes, onChange: setOwnerCodes },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.ownerCodes`>({
    name: `${blockPath}.ownerCodes`,
  });

  const {
    field: { value: maxItems, onChange: setMaxItems },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.maxItems`>({
    name: `${blockPath}.maxItems`,
  });

  const {
    field: { value: paginateMaxItems, onChange: setPaginateMaxItems },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.paginateMaxItems`>({
    name: `${blockPath}.paginateMaxItems`,
  });

  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<DMAutoBlockModel>, `${typeof blockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  useEffect(() => {
    const ctx = Api.getDimuOwners();
    const ctx2 = Api.getDimuOwners('sv');

    Promise.all([ctx.fetchDirect(null), ctx2.fetchDirect(null)])
      .then(([res1, res2]) => {
        const allOwners = [...(res1?.owners || []), ...(res2?.owners || [])];
        setAllDimuOwners(allOwners);
        if (ownerCodes) {
          const ownerList = ownerCodes.split(',');
          setSelectedOwners(allOwners.filter((o) => ownerList.some((oc) => oc === o.identifier)));
        }
      })
      .finally(() => {
        ctx.abort();
        ctx2.abort();
      });
  }, [ownerCodes]);

  const handleOwnerCodesChanged = (owners: Array<DimuOwner>) => {
    setSelectedOwners(owners);
    setOwnerCodes(owners.map((o) => o.identifier).join(','));
  };

  const getSortDirectionLabel = (dir: SortDirection) => {
    if (currentBlock?.sortBy === DMSortType.PUBLISHED_AT || currentBlock?.sortBy === DMSortType.UPDATED_AT) {
      return tCommon(`SortDirections.date_${dir}`);
    }
    if (currentBlock?.sortBy === DMSortType.TITLE) return tCommon(`SortDirections.text_${dir}`);
    return tCommon(`SortDirections.${dir}`);
  };

  return (
    <>
      <M24PageFinder
        hideSiteSelector
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />
      <Container mt={2} gap={2} column top left fullWidth>
        <FormControl sx={{ width: '100%' }}>
          <OutlinedContainer
            fullWidth
            column
            left
            label={tComp('DMBlock.SearchConfig')}
            sx={{
              backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
              paddingTop: '2em',
            }}>
            <Container fullWidth left gap={2}>
              <SelectInput
                options={Object.values(DMObjectType)}
                getOptionKey={(o) => o}
                getOptionLabel={(o) => tCommon(`DMContentType.${o}`)}
                path={`${blockPath}.objectTypes`}
                label={tComp('DMBlock.ObjectTypesLabel')}
                size='small'
                multiSelect
                initialMultiSelectValue={currentBlock?.objectTypes}
              />
              <SelectInput
                options={Object.values(MediaLicense).filter((l) => !!l)}
                getOptionKey={(o) => o}
                getOptionLabel={(o) => tCommon(`MediaLicense.Label.${o}`)}
                path={`${blockPath}.licenseTypes`}
                renderValue={(v) => (typeof v === 'string' ? v : v.join(','))}
                label={tComp('DMBlock.LicenseTypesLabel')}
                size='small'
                multiSelect
                initialMultiSelectValue={currentBlock?.licenseTypes}
              />
            </Container>
            <Container fullWidth mt={2} mb={2} left gap={2}>
              <TextInput path={`${blockPath}.query`} defaultValue='' label={tComp('DMBlock.QueryLabel')} size='small' />
              <TextInput
                path={`${blockPath}.subjects`}
                defaultValue=''
                label={tComp('DMBlock.SubjectsLabel')}
                size='small'
              />
            </Container>
            <div style={{ alignSelf: 'flex-start', marginLeft: '1em' }}>
              <SwitchInput path={`${blockPath}.hasPictures`} label={tComp('DMBlock.HasPictures')} />
            </div>
            <div style={{ alignSelf: 'flex-start', marginBottom: '1em', marginLeft: '1em' }}>
              <SwitchInput path={`${blockPath}.useOwnerCodes`} label={tComp('DMBlock.UseOwnerCodes')} />
            </div>
            {currentBlock?.useOwnerCodes && !!allDimuOwners?.length && (
              <Autocomplete
                multiple
                options={allDimuOwners}
                getOptionLabel={getOptionLabel}
                value={selectedOwners}
                size='small'
                renderOption={(props, option, { selected }) => (
                  <li {...props} style={autocompleteItemStyle(selected)}>
                    {getOptionLabel(option)}
                  </li>
                )}
                fullWidth
                disableCloseOnSelect
                isOptionEqualToValue={(o, v) => o.identifier === v.identifier}
                renderInput={(params) => <TextField {...params} label={tComp('DMBlock.OwnerCodesLabel')} />}
                onChange={(_, v) => handleOwnerCodesChanged(v)}
                renderValue={(value: DimuOwner[], getItemProps) =>
                  value.map((option: DimuOwner, index: number) => (
                    <Chip size='small' {...getItemProps({ index })} key={option.identifier} label={option.identifier} />
                  ))
                }
              />
            )}{' '}
            <OutlinedContainer
              fullWidth
              column
              left
              sx={{
                backgroundColor: Styles.Colors.WHITE,
                paddingTop: '1em',
              }}>
              <Container gap={2} pt={1} fullWidth left>
                <SelectInput
                  options={Object.values(DMSortType)}
                  getOptionKey={(o) => o}
                  getOptionLabel={(o) => tCommon(`DMSortTypes.${o}`)}
                  path={`${blockPath}.sortBy`}
                  label={tCommon('SortTypeLabel')}
                  defaultValue={DMSortType.RELEVANCE}
                  size='small'
                  hideNoSelectionOption
                />
                <SelectInput
                  options={Object.values(SortDirection)}
                  getOptionKey={(o) => o}
                  getOptionLabel={getSortDirectionLabel}
                  path={`${blockPath}.order`}
                  disabled={!currentBlock?.sortBy || currentBlock?.sortBy === DMSortType.RELEVANCE}
                  label={tCommon('SortDirectionLabel')}
                  defaultValue={currentBlock?.sortBy === DMSortType.TITLE ? SortDirection.ASC : SortDirection.DESC}
                  size='small'
                  hideNoSelectionOption
                />
              </Container>
              <Container gap={2} fullWidth left mt={2} mb={2}>
                <TextField
                  type='number'
                  size='small'
                  value={maxItems ?? 0}
                  onChange={(v) => setMaxItems(Number(v.target.value) || undefined)}
                  fullWidth
                  label={tComp('DMBlock.MaxItemsLabel')}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      max: 24,
                    },
                    inputLabel: {
                      shrink: !!Number(maxItems),
                    },
                  }}
                />

                <TextField
                  type='number'
                  size='small'
                  value={paginateMaxItems ?? 0}
                  onChange={(v) => setPaginateMaxItems(Number(v.target.value) || undefined)}
                  fullWidth
                  label={tComp('DMBlock.PaginateMaxItemsLabel')}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      max: 1000,
                    },
                    inputLabel: {
                      shrink: !!Number(paginateMaxItems),
                    },
                  }}
                />
              </Container>
            </OutlinedContainer>
          </OutlinedContainer>
        </FormControl>

        <OutlinedContainer fullWidth column left label={tComp('KPBlock.DisplayOptionsModuleLabel')}>
          <Container fullWidth left pt={1} wrap gap={2}>
            {currentBlock?.view === DMViewType.GRID && (
              <div style={{ width: '150px' }}>
                <SelectInput
                  hideNoSelectionOption
                  defaultValue='auto'
                  options={gridSizeOptions}
                  getOptionKey={(gridSize) => `size-${gridSize}`}
                  getOptionLabel={(gridSize) => `${gridSize}`}
                  path={`${blockPath}.gridSize`}
                  label={tComp('CardModuleBlock.GridSizeLabel')}
                />
              </div>
            )}
            {currentBlock?.view === DMViewType.GRID && (
              <div style={{ width: '250px' }}>
                <SelectInput
                  hideNoSelectionOption
                  defaultValue='auto'
                  options={mobileGridOptions}
                  getOptionKey={(o) => `mobileGrid-${o}`}
                  getOptionLabel={(o) => tComp(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
                  path={`${blockPath}.gridMobileBehaviour`}
                  label={tComp('CardModuleBlock.GridMobileBehaviourLabel')}
                />
              </div>
            )}

            <SwitchInput
              fullWidth={false}
              path={`${blockPath}.showInSameTab`}
              label={tComp('DMFilterSearch.ShowInSameTab')}
            />
            {isSuperAdmin && (
              <Container column left sx={{ width: '300px' }}>
                <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
                  <SwitchInput path={`${blockPath}.integratedView`} label={tComp('KPBlock.IntegratedView')} />
                </div>
              </Container>
            )}
          </Container>
        </OutlinedContainer>

        {currentBlock?.view !== DMViewType.MAP && (
          <OutlinedContainer fullWidth column left label={tComp('DMBlock.DmLinkLabel')}>
            <Container fullWidth left pt={1} gap={2}>
              <TextInput path={`${blockPath}.dmLink`} defaultValue='' label={tComp('DMBlock.DmLink')} size='small' />
              <TextInput
                path={`${blockPath}.dmLinkText`}
                defaultValue=''
                label={tComp('DMBlock.DmLinkText')}
                size='small'
              />
            </Container>
          </OutlinedContainer>
        )}

        <OutlinedContainer fullWidth column left label={tComp('EventsBlock.ShowAllLinkPageTitle')}>
          {showAllLinkPage && (
            <PageListManualBlockItem
              index={0}
              onReorder={noop}
              onDelete={() => onChangeShowAllLinkPage(null)}
              overrideItemPath={`${blockPath}.showAllLinkPage`}
              color={Styles.Colors.LIGHT_GREY}
              hideHighlightCheckbox
              hideSkinSelector
              disableReordering
            />
          )}
          <Button onClick={() => setPageFinderOpen(true)} variant='outlined' color='secondary'>
            {tComp('EventsBlock.ShowAllLinkPageButtonLabel')}
          </Button>
        </OutlinedContainer>

        {/* currentBlock?.view === DMViewType.MAP && getMapSettings(tComp, blockPath) */}
      </Container>
    </>
  );
};

export default DMAutoBlock;
